import {Grid, Paper, Typography} from '@mui/material';
import MuiAppBar from '@mui/material/AppBar';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Toolbar from '@mui/material/Toolbar';
import {styled} from '@mui/material/styles';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {drawerPaddingRight, drawerWidth} from '../navbar/navbar';
import {ButtonColor, PrimaryColor} from '../theme/theme';
import ListItemText from '@mui/material/ListItemText';
import MuiToggleButton from "@mui/material/ToggleButton";

import {makeStyles} from "@mui/styles";

import "./header.css";
import Box from "@mui/material/Box";
import {useLocation} from 'react-router';
import {useAuth} from "../../views/AuthProvider/AuthProvider";
import moment from "moment";
import {Link} from 'react-router';
import {Avatar, Skeleton} from "@mui/material";
import {fetchNotifications, patchNotification, fetchCustomers, fetchJobsByCustomer} from "../../utils/apiCalls";
import {fetchTapeStatusCount} from "../../utils/dashboardCalls";
import ContactForm from "../contact/ContactForm";
import {CollapsibleGroupedSelect, StyledButtonContained, StyledButtonOutlined} from "../../utils/uiUtils";
import {useJobContext} from "../../views/JobContextProvider/JobContextProvider";
import {buildJobGroups, buildOwnJobGroup} from "../../views/JobContextProvider/jobGroupsBuilder";

/**
 * A media count narrow enough to sit in the job selector's badge.
 *
 * Abbreviated rather than grouped with separators because the badge is a fixed pill beside the job
 * reference, and a customer with a quarter of a million tapes would otherwise either overflow it or
 * force every badge wide enough for the worst case. One decimal below ten thousand so that 1,200 and
 * 1,900 stay distinguishable — above that the leading digits carry the sense and the tail does not.
 *
 * The exact figure is still available: the badge carries it as its tooltip and its accessible name.
 *
 * @param {number} count — media in the job, as returned by man_item_count
 * @returns {string} the badge text
 */
export const formatJobMediaCount = (count) => {
    // null explicitly, because Number(null) is 0 and "no figure" must not print as "no media".
    const n = count === null ? NaN : Number(count);
    if (!Number.isFinite(n) || n < 0) return '–';
    if (n < 1000) return String(Math.round(n));
    if (n < 10000) return `${(Math.round(n / 100) / 10).toString().replace(/\.0$/, '')}k`;
    if (n < 1000000) return `${Math.round(n / 1000)}k`;
    return `${(Math.round(n / 100000) / 10).toString().replace(/\.0$/, '')}m`;
};

const BorderDiv = styled('div')({
    position: 'absolute',
    width: drawerWidth,
    height: '100%',
    borderBottom: 'none',
    zIndex: 2,
    paddingRight: drawerPaddingRight
});
const StyledPaper = styled(Paper)(({theme}) => ({
    backgroundColor: theme.palette.mode === 'dark' ? '#49484E' : 'rgba(247, 247, 249, 0.5)',
    ...theme.typography.body2,
    padding: theme.spacing(2),
    maxWidth: 400,
    color: theme.palette.text.primary,
}));
const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open',
})(({theme, open}) => ({
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
        marginLeft: drawerWidth,
        width: `calc(100% - ${drawerWidth}px)`,
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    }),
}));


export const Header = () => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [showAllNotification, setShowAllNotification] = useState(true);
    const [notificationData, setNotificationData] = useState(null);
    const open = Boolean(anchorEl);
    const classes = useStyles();
    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };
    const [filter, setFilter] = useState("");
    const [date, setDate] = useState("month");

    const handleChangeFilter = (event) => {
        setFilter(event.target.value);
    }
    const handleChange = (event, newAlignment) => {
        setDate(newAlignment);
    };
    const Item = styled(Paper)(({theme}) => ({
        backgroundColor: "transparent",
        border: "none",
        boxShadow: "none",
        color: '#fff',
        textAlign: 'left',
    }));

    // ── Job context selector ──────────────────────────────────────────────────
    /*
     * The groups live in the context rather than in this component's own state.
     *
     * Still fetched here — the header owns the selector, and the two roles read the list from different
     * endpoints — but published, because the list is no longer only the selector's business. A dashboard
     * widget reporting on "All Jobs" has to know which jobs those are; see the comment on `jobGroups` in
     * JobContextProvider for what it does with them.
     */
    const {selection, setSelection, isMulti, selectorEnabled, jobGroups, setJobGroups} = useJobContext();
    const {user, isAdmin, logout} = useAuth();

    useEffect(() => {
        let cancelled = false;
        /*
         * Announced before the request goes out, and resolved on every path out of it — success, failure,
         * and the case where there is nothing to fetch at all. A consumer scoping itself by these jobs
         * waits on this rather than counting the whole platform first and correcting itself a moment later,
         * so leaving it unresolved would leave that consumer waiting for a list that is not coming.
         */
        setJobGroups(undefined);
        /*
         * Off the loading wash, both branches. This is the selector populating itself on mount, not
         * anything the reader asked for, and it is the longest of the calls a page load makes: for an
         * admin it is every customer with their linked orders, and for a customer role it paginates
         * their whole job list. Unmarked it washed the entire portal out — the sidebar with it — so
         * navigating away from a slow dashboard was impossible until it answered, which is the
         * blocking that was reported. It also gates all three graph widgets through
         * useDashboardJobScope's `waiting`, so it is the one call on this page worth being careful
         * about: while it is in flight there is nothing on screen to click *and* nothing loading.
         */
        if (isAdmin) {
            // Admin: /customers/ returns all customers with linked_orders.
            fetchCustomers("", null, {background: true})
                .then(data => { if (!cancelled) setJobGroups(buildJobGroups(data)); })
                .catch(error => {
                    console.log("Error fetching customers for job selector:", error);
                    if (!cancelled) setJobGroups([]);
                });
        } else if (user?.customer_uuid) {
            // Customer role: /customers/ is superuser-only; fetch own jobs instead.
            fetchJobsByCustomer(user.customer_uuid, {background: true})
                .then(jobs => { if (!cancelled) setJobGroups(buildOwnJobGroup(jobs, user.arkbridge_customer_id, user.company_name)); })
                .catch(error => {
                    console.log("Error fetching own jobs for job selector:", error);
                    if (!cancelled) setJobGroups([]);
                });
        } else {
            setJobGroups([]);
        }
        return () => { cancelled = true; };
    }, [isAdmin, user?.customer_uuid, user?.arkbridge_customer_id, user?.company_name, setJobGroups]);

    /*
     * How much media each job holds, keyed `${cid}:${jobId}`.
     *
     * The badge used to show `item.key` — the raw ArkBridge job id — which is an internal identifier
     * nobody picking a job needs, and reading like a quantity beside the job reference it invited
     * exactly the sum-them-up misreading it cannot support. The count is the figure that badge position
     * was implying, so it is the figure it now carries.
     *
     * Keyed by cid as well as job id because an admin's selector spans customers and job ids are only
     * unique within one; keeping the cid in the key also means switching customer cannot show a figure
     * fetched for another.
     *
     * Three states per key: absent (not fetched, or in flight) draws a skeleton, a number draws the
     * figure, null draws a dash. The dash is deliberate rather than a zero — a request that failed and
     * a job that genuinely holds nothing must not look alike.
     */
    const [jobCounts, setJobCounts] = useState({});
    // Which keys have been asked for, so re-opening the menu does not re-ask. A ref, not state,
    // because it must be current the moment the next callback fires rather than after a re-render.
    const requestedCounts = useRef(new Set());

    // Anything held is about the previous set of jobs — a different customer, or a fresh login.
    useEffect(() => {
        requestedCounts.current = new Set();
        setJobCounts({});
    }, [jobGroups]);

    /*
     * Fetches the counts for one group's jobs, called by the selector as a group is expanded.
     *
     * Per group and not for every group, because an admin's selector holds a group per customer and
     * each job is its own count query: fetching the lot on mount would be a request per job in the
     * system, on every page load, for a dropdown that may never be opened. Expanding a group is the
     * user saying which jobs they are looking at.
     *
     * `background: true` keeps these off the global loading wash (utils/loadingWash.js) — nobody is
     * waiting on a badge, and a screen-holding count would freeze the portal while the menu is open.
     * `quiet: true` for the same reason in the other direction: a badge that cannot be drawn is not
     * worth a red banner over the page. Cached, unlike the Media Metrics card's counts, since a badge
     * makes no liveness claim.
     */
    const loadCountsForGroup = useCallback((groupKey) => {
        const group = jobGroups.find(g => g.key === groupKey);
        if (!group) return;
        group.items.forEach((item) => {
            const countKey = `${groupKey}:${item.key}`;
            if (requestedCounts.current.has(countKey)) return;
            requestedCounts.current.add(countKey);
            fetchTapeStatusCount(undefined, groupKey, {jobIds: [item.key], background: true, quiet: true})
                .then(count => setJobCounts(prev => ({...prev, [countKey]: Number(count) || 0})))
                .catch(() => {
                    // Dropped from the requested set so re-opening the menu tries again — the usual
                    // cause is a request that was in flight when the page navigated.
                    requestedCounts.current.delete(countKey);
                    setJobCounts(prev => ({...prev, [countKey]: null}));
                });
        });
    }, [jobGroups]);

    const renderJobMediaBadge = (item, groupKey) => {
        const count = jobCounts[`${groupKey}:${item.key}`];
        const badgeStyle = {
            width: 44, height: 24, borderRadius: '12px', fontSize: 11, fontFamily: 'Poppins',
            bgcolor: 'rgba(255,197,64,0.15)', border: '1px solid rgba(255,197,64,0.4)', color: '#FFC540',
        };
        if (count === undefined) {
            return (
                <Skeleton
                    variant="rounded"
                    data-testid={`job-count-loading-${item.key}`}
                    sx={{width: 44, height: 24, borderRadius: '12px', bgcolor: 'rgba(255,197,64,0.12)'}}
                />
            );
        }
        // The count in the tooltip and the accessible name in full, since the badge itself abbreviates.
        const exact = count === null ? 'Media count unavailable' : `${count.toLocaleString()} media`;
        return (
            <Avatar sx={badgeStyle} title={`${item.label}: ${exact}`} aria-label={exact}>
                {count === null ? '–' : formatJobMediaCount(count)}
            </Avatar>
        );
    };

    /*
     * The selector, in the one place that owns it.
     *
     * `isMulti` comes from the context rather than being written here so that the selector and everything
     * scoping itself by the selection cannot disagree about what a selection may hold. With it on, the
     * company headers carry a checkbox that takes every job beneath them, jobs are pickable one at a time,
     * and — no `singleGroup` — a selection may span companies, which is what was asked for.
     *
     * The menu stays open on each pick, which is the point: choosing four jobs is one act, and a menu that
     * closed after the first would make it four.
     */
    const renderJobSelector = () => (
        <CollapsibleGroupedSelect
            title="Job"
            groups={jobGroups}
            value={selection}
            onChange={setSelection}
            isMulti={isMulti}
            disabled={!selectorEnabled}
            noSelectionText="All Jobs"
            selectedDisplay="item-only"
            /*
             * One job reference spelled out, the rest counted. The field sits in the header row with
             * Support Request and Logout beside it, and spelling out a second reference widens it enough to
             * shove those across — so the toolbar shifted as jobs were ticked, and the jump back on the
             * third pick (where the count took over) read as the layout breaking. One label plus ' +N' is
             * the same width at every selection.
             */
            maxDisplayedItems={1}
            minWidth={180}
            size="sm"
            onGroupExpanded={loadCountsForGroup}
            renderItem={(item, groupKey, isSelected) => (
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
                    {renderJobMediaBadge(item, groupKey)}
                    <Typography sx={{fontSize: 13, color: isSelected ? '#FFC540' : '#FFFFFF', fontFamily: 'Poppins'}}>{item.label}</Typography>
                </Box>
            )}
        />
    );

    const [isContactFormOpen, setIsContactFormOpen] = useState(false);

    const openContactForm = () => {
        setIsContactFormOpen(true);
    }

    const closeContactForm = () => {
        setIsContactFormOpen(false);
    }


    /*
     * Off the loading wash: the bell fills itself in, on mount and again after one is marked read.
     * Nobody is waiting on the count, and holding the portal for it means a reader who never opens
     * the bell still pays for it on every page load.
     */
    function updateNotificationData() {
        fetchNotifications({background: true})
            .then(data => {
                data?.results?.notifications?.reverse();
                setNotificationData(data);
            })
            .catch(error => console.log("Error fetching notifications in header:", error));
    }

    useEffect(updateNotificationData, []);

    const logOut = () => {
        logout();
    }

    const markAsUnread = (notification) => {
        patchNotification(notification.uuid, {read: true})
            .then(updateNotificationData)
            .catch(error => console.error("Error marking notification as read:", error));
    }

    // To Determine the Title which appears on the AppBar
    const location = useLocation();
    const isRouteActive = useMemo(() => {
        return (routes) => {
            return routes.some((route) => location.pathname.split('/')[1] === route);
        };
    }, [location.pathname]);

    const navItems = [
        {label: 'Home', iconUrl: '/assets/img/home.png', highlightIconUrl: '/assets/img/homeHighlight.png', to: '/', viewUrls: ['', 'Home']},
        {label: 'Jobs', iconUrl: '/assets/img/order.png', highlightIconUrl: '/assets/img/orderHighlight.png', to: '/Jobs', viewUrls: ['Jobs', 'JobTracking', 'CustomerJobs']},
        {label: 'My Team', iconUrl: '/assets/img/account.png', highlightIconUrl: '/assets/img/accountHighlight.png', to: '/Team', viewUrls: ['Team']},
        {label: 'All Users', iconUrl: '/assets/img/account.png', highlightIconUrl: '/assets/img/accountHighlight.png', to: '/AllUsers', viewUrls: ['AllUsers']},
        {label: 'All Customers', iconUrl: '/assets/img/account.png', highlightIconUrl: '/assets/img/accountHighlight.png', to: '/Customers', viewUrls: ['Customers', 'AccountCreation']},
        // Hidden, not deleted: Microsoft's approval is now the customer's to give, so Tape Ark no longer sends it. See the /MicrosoftSignIn route in App.js.
        // {label: 'Microsoft Sign-in', iconUrl: '/assets/img/account.png', highlightIconUrl: '/assets/img/accountHighlight.png', to: '/MicrosoftSignIn', viewUrls: ['MicrosoftSignIn']},
        {label: 'Job', iconUrl: '/assets/img/order.png', highlightIconUrl: '/assets/img/orderHighlight.png', to: '/JobCreation', viewUrls: ['JobCreation']},
        {label: 'Settings', iconUrl: '/assets/img/settings.png', highlightIconUrl: '/assets/img/settingsHighlight.png', to: '/Settings', viewUrls: ['Settings']},
        {label: 'Data Management', iconUrl: '/assets/img/restore.png', highlightIconUrl: '/assets/img/restore.png', to: '/DataManagement', viewUrls: ['DataManagement', 'Restore']}
    ];

    const renderTopLogoWithCurrentLocation = () => {
        return <Box display='inline-flex' gap={4} className='col-md-2'>
            <Link to='/' style={{textDecoration: 'none', zIndex: 2}}>
                <img width={'40px'}
                     height={'40px'}
                     alt="tapeark"
                     src={process.env.PUBLIC_URL + '/assets/img/tapeark_logo_inv.svg'}/>
            </Link>
            <Typography sx={{fontFamily: 'Lato', fontWeight: 700, fontSize: 28, lineHeight: '33px', letterSpacing: '0em'}}>
                {navItems.find((item) => isRouteActive(item.viewUrls))?.label}
            </Typography>
        </Box>;
    }

    const renderNotifications = () => {
        return <Menu
            className='bg-color'
            id="basic-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            MenuListProps={{
                'aria-labelledby': 'basic-button',
            }}
            PaperProps={{
                style: {
                    width: 587,
                    height: "max-content",
                    maxHeight: 524,
                    left: 895,

                },
            }}
        >
            <Box sx={{backgroundColor: "#3A3E3F"}}>
                <Grid p={2}>
                    <Grid marginLeft={0} container spacing={1} alignItems="center" className='no-box' boxShadow="none">
                        {
                            notificationData?.results?.notifications?.length ?
                                <>
                                    <Grid item xs={6}>
                                        <Item className='heading-notify'>Your Notifications</Item>
                                    </Grid>
                                    <Grid item xs={6} paddingBottom={2} direction="row" display="flex" justifyContent="flex-end">
                                        <Item><StyledButtonOutlined sx={{
                                            cursor: 'pointer'
                                        }} variant="outlined" className='clr-btn' onClick={() => setNotificationData([])}>Clear</StyledButtonOutlined></Item>
                                    </Grid>
                                </> :
                                <Grid item xs={6}>
                                    <Item className='heading-notify'>No Notifications</Item>
                                </Grid>
                        }
                    </Grid>
                    {
                        notificationData?.results?.notifications?.length ?
                            <hr className='headerHr'/> : ""
                    }

                </Grid>
                {notificationData?.results?.notifications?.length ?
                    <>
                        <Grid direction='row' p={2}>
                            <Button variant={showAllNotification ? "outlined" : null} className={showAllNotification ? 'activeFilterButton' : 'disableFilterButton'} onClick={() => setShowAllNotification(true)} sx={{
                                cursor: 'pointer'
                            }}>
                                All
                            </Button>
                            <Button variant={!showAllNotification ? "outlined" : null} className={!showAllNotification ? 'activeFilterButton' : 'disableFilterButton'} onClick={() => setShowAllNotification(false)} sx={{
                                cursor: 'pointer'
                            }}>Unread</Button>
                        </Grid>
                        <Grid container marginLeft={0} paddingBottom={2} spacing={1} p={2} marginTop={3} alignItems="center" className='no-box bg-box'>
                            {notificationData?.results?.notifications?.map((noti, i) => {

                                return (
                                    ((showAllNotification) || (!showAllNotification && !noti?.read)) &&
                                    <>
                                        <Link to={`/JobTracking/${noti.uuid}`} state={{data: noti}} style={{width: '100%', textDecoration: 'none', color: 'transparent'}}>
                                            <Grid key={i} container direction="row" onClick={() => markAsUnread(noti)}
                                                  className={noti?.read ? 'readNotification' : null} sx={{
                                                cursor: 'pointer'
                                            }}>
                                                <Grid item xs={8} marginBottom={3}>
                                                    <MenuItem>
                                                        <ListItemText className='list-item'>{noti.description}</ListItemText>
                                                    </MenuItem>
                                                    <p className='time-txt' p="0">{moment(noti.created_at).format("ddd MMM/DD/YYYY, hh:mm A")}</p>
                                                </Grid>
                                                {!noti?.read && <Grid item xs={4} direction="row" display="flex" justifyContent="flex-end" alignItems="center">
                                                    <img src="/assets/img/circledot.svg" alt="" height={15}/>
                                                </Grid>}

                                            </Grid>
                                        </Link>
                                        <hr className='width-hr'/>
                                    </>
                                )

                            })
                            }
                        </Grid>
                    </> : ""}
            </Box>
        </Menu>;
    }

    const renderLogoutButton = () => {
        return <>
            <Link to='/' style={{textDecoration: 'none'}}>
                <StyledButtonContained
                    variant='contained'
                    sx={{height: '40px', cursor: 'pointer'}}
                    onClick={logOut}>
                    Logout
                </StyledButtonContained>
            </Link>
        </>;
    };

    const renderMakeRequestButton = () => {
        return <>
            <StyledButtonContained
                variant='contained'
                sx={{height: '40px', cursor: 'pointer'}}
                onClick={openContactForm}>
                Support Request
            </StyledButtonContained>
        </>;
    };


    return (
        <>
            <AppBar position="fixed" sx={{width: '100%', backgroundColor: PrimaryColor, borderBottom: 'none', boxShadow: '0px 4px 11px 0px #0000001A'}}>
                <BorderDiv/>
                <Toolbar sx={{flexGrow: 1, display: 'flex', justifyContent: 'space-between'}}>
                    {renderTopLogoWithCurrentLocation()}
                    <div className='col-md-10'>
                        <Box components="div"
                             sx={{
                                 display: 'flex',
                                 alignItems: 'center',
                                 gap: 1
                             }}>
                            {renderJobSelector()}
                            {renderNotifications()}
                            {renderMakeRequestButton()}
                            {renderLogoutButton()}
                        </Box>
                        <ContactForm
                            isFormOpen={isContactFormOpen}
                            closeForm={closeContactForm}
                            contactType='help'
                        />
                    </div>
                </Toolbar>
            </AppBar>
        </>
    )
}
export const ToggleButton = styled(MuiToggleButton)({
    "&.MuiToggleButton-root.Mui-selected": {
        backgroundColor: ButtonColor,
        // borderRadius: "8px",
        color: '#404558',
        fontWeight: 600,
        fontSize: "14px",
        border: `1px solid #000`,
    },
    "&.MuiToggleButton-root": {
        // color: "#404558",
        borderRadius: "8px",
        textTransform: "none !important",
        height: "35px",
        fontSize: "14px",
        width: "116px",
        border: "0 !important",
        // border-radius: 8px;
        background: "#E1E8F080",
        color: "#404558"
    },
});

const useStyles = makeStyles({
    mainContainer: {
        minHeight: '565px',
        maxHeight: '565px',
        borderRadius: 8,
        border: '1px solid #4a5058',
        background: '#3A3E3F',

    },
    mainHeading: {
        fontSize: '20px',
        fontWeight: 700,
        lineHeight: '24px',
        color: '#FFFFFF'
    },
    closeIcon: {
        color: '#969696'
    },
    secondHeading: {
        fontWeight: 400,
        fontSize: '42px !important',
        lineHeight: '51px',
        color: '#FFFFFF',
    },
    years: {
        fontWeight: 400,
        fontSize: '14px',
        lineHeight: '17px',
        color: '#FFFFFF'
    },
    data: {
        fontWeight: 400,
        fontSize: '14px',
        lineHeight: '17px',
        color: '#FFFFFF'
    },
    per: {
        fontWeight: 400,
        fontSize: '14px',
        lineHeight: '17px',
        color: '#697588'
    }
});